// Kompas Dnia — PL/EN dictionaries.
// Keep flat, additive; nested only when it improves readability.

export const translations = {
  pl: {
    app: {
      title: "Kompas Dnia",
      tagline: "AI Decision Dashboard",
    },
    nav: {
      today: "Dziś",
      dashboard: "Dashboard",
      history: "Historia",
      reflection: "Refleksja",
      settings: "Ustawienia",
    },
    common: {
      soon: "Wkrótce",
      back: "Wróć",
      backToSettings: "Wróć do ustawień",
      cancel: "Anuluj",
      confirm: "Potwierdź",
      delete: "Usuń",
      save: "Zapisz",
    },
    onboarding: {
      title: "Kompas Dnia",
      subtitle: "Twój codzienny kierunek w kilka minut.",
      description:
        "Zatrzymaj się na chwilę, sprawdź swój stan i wybierz najlepszy kierunek dnia: działaj, odpocznij, trzymaj kierunek, zmień strategię albo zatrzymaj się.",
      points: [
        "Sprawdź energię, presję i jasność.",
        "Otrzymaj prostą decyzję dnia.",
        "Zakończ check-in z większym spokojem.",
      ],
      cta: "Kontynuuj jako gość",
      guestNote:
        "Nie tworzymy konta. Twoje dane zostają lokalnie na tym urządzeniu.",
      settingsLink: "Ustawienia",
    },
    settings: {
      title: "Ustawienia",
      subtitle: "Twoje preferencje i dane.",
      language: {
        heading: "Język",
        description: "Wybierz język aplikacji.",
      },
      premium: {
        heading: "Premium — wkrótce",
        description:
          "Dłuższa historia, raporty tygodniowe i miesięczne oraz głębsze refleksje AI pojawią się w przyszłości.",
      },
      about: {
        heading: "O aplikacji",
        description:
          "Aplikacja do codziennej refleksji i wyboru kierunku dnia. Dane trzymane są lokalnie na Twoim urządzeniu. To nie jest narzędzie medyczne ani terapeutyczne.",
        version: "Wersja 0.1.0 MVP",
      },
      legal: {
        heading: "Regulaminy",
        privacy: "Polityka prywatności",
        terms: "Regulamin",
        deleteData: "Usuń dane / konto",
        aiNotice: "Informacja o AI",
      },
      data: {
        heading: "Dane",
        description:
          "Wszystkie Twoje dane zapisane są lokalnie w tej przeglądarce.",
        deleteButton: "Usuń dane lokalne",
        confirmTitle: "Usunąć wszystkie dane?",
        confirmDescription:
          "Ta operacja usunie wszystkie Twoje check-iny i ustawienia z tego urządzenia. Nie można jej cofnąć.",
        confirmAction: "Tak, usuń",
        toastDeleted: "Dane lokalne zostały usunięte.",
      },
    },
    placeholder: {
      today: {
        title: "Dzisiejszy check-in",
        note: "Twój codzienny kompas pojawi się tutaj wkrótce.",
      },
      result: {
        title: "Wynik dnia",
        note: "Twoja decyzja i rekomendacja pokażą się tutaj po check-inie.",
      },
      dashboard: {
        title: "Dashboard",
        note: "Podgląd Twoich trendów i równowagi. Wkrótce.",
      },
      history: {
        title: "Historia",
        note: "Twoje wcześniejsze check-iny pojawią się tutaj.",
      },
      reflection: {
        title: "Refleksja",
        note: "Głębsza refleksja nad Twoim tygodniem. Wkrótce.",
      },
    },
    legal: {
      privacy: {
        title: "Polityka prywatności",
        note: "Pełna treść pojawi się wkrótce.",
      },
      terms: {
        title: "Regulamin",
        note: "Pełna treść pojawi się wkrótce.",
      },
      deleteData: {
        title: "Usuwanie danych",
        note: "Instrukcja usuwania danych pojawi się wkrótce.",
      },
      aiNotice: {
        title: "Informacja o AI",
        note: "Informacja o wykorzystaniu AI pojawi się wkrótce.",
      },
    },
    checkin: {
      title: "Check-in",
      submit: "Pokaż mój kierunek",
      submitLoading: "Chwila…",
      counter: "{count}/240",
      alreadyDone: {
        eyebrow: "Twój dzisiejszy kierunek",
        title: "Zrobiłeś dziś check-in",
        description:
          "Twój dzisiejszy kierunek to już wybrany. Możesz go zobaczyć lub przejść check-in ponownie.",
        view: "Zobacz wynik",
        redo: "Zrób check-in ponownie",
      },
      questions: {
        energy: {
          question: "Ile masz dziś energii?",
          min: "1 — bardzo mało",
          max: "5 — bardzo dużo",
        },
        mood: {
          question: "Jaki jest Twój dzisiejszy nastrój?",
          min: "1 — ciężki",
          max: "5 — lekki/dobry",
        },
        pressure: {
          question: "Jak mocno czujesz dziś presję?",
          min: "1 — prawie wcale",
          max: "5 — bardzo mocno",
        },
        clarity: {
          question: "Czy masz dziś jasny priorytet?",
          min: "1 — zupełnie nie",
          max: "5 — bardzo jasno",
        },
        planRealism: {
          question: "Czy dzisiejszy plan jest realistyczny?",
          min: "1 — nierealny",
          max: "5 — bardzo realistyczny",
        },
        need: {
          question: "Czego najbardziej dziś potrzebujesz?",
          options: {
            action: "działania",
            rest: "odpoczynku",
            direction: "utrzymania kierunku",
            strategy: "korekty strategii",
            pause: "zatrzymania się",
          },
        },
        dailyWin: {
          question:
            "Jaka jedna rzecz byłaby dziś największym zwycięstwem?",
          placeholder: "Możesz to pominąć.",
        },
      },
    },
    result: {
      eyebrow: "Twój kierunek na dziś",
      balanceLabel: "Równowaga dnia",
      recommendationLabel: "Rekomendacja",
      dailyWinLabel: "Twoje dzisiejsze zwycięstwo",
      toastSaved: "Zapisano",
      actions: {
        redo: "Zrób check-in ponownie",
        dashboard: "Przejdź do dashboardu",
      },
      labels: {
        act: {
          title: "Działaj",
          description:
            "Masz dziś wystarczająco dużo energii i jasności, żeby ruszyć do przodu.",
        },
        rest: {
          title: "Odpocznij",
          description:
            "Dzisiejszy dzień może wymagać spokojniejszego tempa i regeneracji.",
        },
        stay: {
          title: "Trzymaj kierunek",
          description:
            "Nie potrzebujesz dziś rewolucji. Najlepszy ruch to spokojna kontynuacja.",
        },
        change: {
          title: "Zmień strategię",
          description:
            "Dzisiejszy plan może wymagać korekty, uproszczenia albo przesunięcia.",
        },
        pause: {
          title: "Zatrzymaj się",
          description:
            "To może być dzień, w którym najważniejsze jest odzyskanie jasności.",
        },
      },
    },
    dashboard: {
      title: "Dashboard",
      today: "Dziś",
      viewDetails: "Zobacz szczegóły",
      newCheckin: "Nowy check-in",
      redoCheckin: "Zrób check-in ponownie",
      emptyState: {
        message:
          "Nie zrobiłeś jeszcze dziś check-inu. Zajmie mniej niż minutę.",
        cta: "Zrób check-in",
      },
      strip: {
        heading: "Ostatnie 7 dni",
        empty: "Brak wpisu",
      },
      weeklySummary: {
        heading: "Podsumowanie tygodnia",
        template:
          "W tym tygodniu najczęściej pojawiał się kierunek: {result}. {phase}",
        averageBalance: "Średnia równowaga: {value}/100",
        notEnough:
          "Za mało danych, żeby pokazać podsumowanie tygodnia. Wróć po kilku check-inach.",
        phases: {
          act: "To może oznaczać, że jesteś w fazie działania — masz warunki, żeby posuwać sprawy do przodu.",
          rest: "To może oznaczać, że jesteś w fazie regeneracji — być może dzień na dzień wybierasz spokojniejsze tempo.",
          stay: "To może oznaczać, że jesteś w fazie kontynuacji — trzymasz kurs, bez potrzeby rewolucji.",
          change:
            "To może oznaczać, że jesteś w fazie korekt — plany wymagają upraszczania i przesunięć.",
          pause:
            "To może oznaczać, że jesteś w fazie zatrzymania — odzyskujesz jasność i kompas.",
        },
      },
    },
    history: {
      title: "Historia",
      empty: {
        message:
          "Twoja historia pojawi się tutaj po pierwszym zapisanym check-inie.",
        cta: "Zrób pierwszy check-in",
      },
      item: {
        recommendationLabel: "Rekomendacja",
        answersLabel: "Odpowiedzi",
        winLabel: "Zwycięstwo dnia",
        reflectionLabel: "Refleksja",
        showMore: "Pokaż więcej",
        showLess: "Ukryj",
      },
      answers: {
        energy: "Energia",
        mood: "Nastrój",
        pressure: "Presja",
        clarity: "Jasność",
        planRealism: "Realizm planu",
        need: "Potrzeba",
      },
      premiumTeaser:
        "Dłuższa historia i raporty miesięczne będą dostępne w Premium — wkrótce.",
    },
    reflection: {
      title: "Refleksja",
      headerToday: "Refleksja na dziś",
      headerOther: "Refleksja: {date}",
      textareaPlaceholder:
        "Zapisz swoje przemyślenia — nikt tego nie zobaczy.",
      saveButton: "Zapisz refleksję",
      counter: "{count}/600",
      empty: {
        message: "Zanim zrobisz refleksję, zrób pierwszy check-in dnia.",
        cta: "Zrób check-in",
      },
      saved: {
        heading: "Twoja notatka z tego dnia",
        edit: "Edytuj",
      },
      toastSaved: "Zapisano refleksję",
      questionsIntro: "Kilka pytań, które mogą pomóc:",
      questions: {
        act: [
          "Co jest dziś jedną rzeczą, która naprawdę przesunie Cię do przodu?",
          "Co możesz odłożyć, żeby nie rozpraszać energii?",
        ],
        rest: [
          "Co naprawdę musi się dziś wydarzyć, a co może poczekać?",
          "Jak możesz zmniejszyć presję bez poczucia winy?",
        ],
        stay: [
          "Jaki mały krok wystarczy dziś wykonać?",
          "Co pomoże Ci nie komplikować prostego dnia?",
        ],
        change: [
          "Która część planu jest dziś zbyt ciężka?",
          "Co możesz uprościć, przesunąć albo usunąć?",
        ],
        pause: [
          "Czego teraz najbardziej potrzebujesz: ciszy, porządku, przerwy czy decyzji?",
          "Jaki jest najmniejszy spokojny krok, który możesz wykonać?",
        ],
      },
    },
    premium: {
      badge: { soon: "Wkrótce" },
      cta: {
        seeIncludes: "Zobacz, co obejmuje Premium",
        learnMore: "Dowiedz się więcej",
      },
      sections: {
        features: "Co znajdzie się w Premium",
      },
      hero: {
        title: "Kompas Dnia Premium",
        subtitle:
          "Więcej historii, raportów i głębszych refleksji — wkrótce.",
        description: {
          p1: "Premium pomoże lepiej rozumieć własny rytm energii, presji, jasności i decyzji. To rozszerzenie dla osób, które chcą widzieć wzorce, wracać do refleksji i korzystać z głębszych insightów.",
          p2: "Darmowa wersja nadal pozostaje użyteczna na co dzień. Premium ma pogłębiać doświadczenie, a nie odbierać podstawową wartość aplikacji.",
        },
      },
      features: {
        fullHistory: {
          title: "Pełna historia",
          description:
            "Dostęp do historii 30, 90 dni i całości — z możliwością powrotu do wcześniejszych refleksji i wpisów.",
        },
        weeklyReports: {
          title: "Raporty tygodniowe",
          description:
            "Tygodniowe podsumowania z częstotliwością wyników i średnim balance score.",
        },
        monthlyReports: {
          title: "Raporty miesięczne",
          description:
            "Miesięczne raporty pokazujące trendy i zmiany w Twoim rytmie.",
        },
        patternAnalysis: {
          title: "Analiza wzorców",
          description:
            "Delikatne insighty o wzorcach energii, presji, jasności i równowagi dnia.",
        },
        deeperReflections: {
          title: "Głębsze refleksje AI",
          description:
            "Bardziej rozbudowane refleksje i delikatna interpretacja wzorców — nadal spokojnym, ludzkim językiem.",
        },
        customQuestions: {
          title: "Własne pytania check-in",
          description:
            "Dodaj własne pytania do codziennego check-inu i personalizuj rytuał.",
        },
        dayModes: {
          title: "Tryby dnia",
          description:
            "Wybierz tryb dopasowany do Twojej sytuacji: Tryb Przedsiębiorcy, Spokojnego Dnia, Głębokiej Pracy, Regeneracji, Decyzji.",
        },
        exportNotes: {
          title: "Eksport notatek",
          description:
            "Eksportuj swoje refleksje, notatki i raporty do zewnętrznego pliku.",
        },
        gentleReminders: {
          title: "Delikatne przypomnienia",
          description:
            "Spokojne przypomnienia o check-inie i tygodniowym podsumowaniu — bez agresywnych powiadomień.",
        },
      },
      dayModes: {
        founder: "Tryb Przedsiębiorcy",
        calmDay: "Tryb Spokojnego Dnia",
        deepWork: "Tryb Głębokiej Pracy",
        recovery: "Tryb Regeneracji",
        decision: "Tryb Decyzji",
      },
      sync: {
        title: "Synchronizacja i backup",
        description:
          "Możliwość synchronizacji i backupu między urządzeniami — rozważana jako opcja w przyszłości. Wersja darmowa zostaje w pełni offline i lokalna.",
      },
      closing:
        "Premium nie ma odbierać wartości darmowej wersji. Zawsze będziesz mógł wykonać dzisiejszy check-in i zobaczyć swój kierunek dnia — to zostaje sercem aplikacji.",
      settingsCard: {
        heading: "Kompas Dnia Premium",
        subtitle:
          "Więcej historii, raportów i głębszych refleksji — wkrótce.",
      },
      dashboardTeaser: {
        title: "Raport miesięczny",
        subtitle: "Twoje wzorce i trendy z ostatnich 30 dni.",
      },
      historyTeaser:
        "Odblokuj dłuższą historię i raporty miesięczne w Kompas Dnia Premium — wkrótce.",
      reflectionTeaser:
        "Głębsze refleksje AI i analiza wzorców będą dostępne w Kompas Dnia Premium.",
    },
  },
  en: {
    app: {
      title: "Daily Compass",
      tagline: "AI Decision Dashboard",
    },
    nav: {
      today: "Today",
      dashboard: "Dashboard",
      history: "History",
      reflection: "Reflection",
      settings: "Settings",
    },
    common: {
      soon: "Coming soon",
      back: "Back",
      backToSettings: "Back to settings",
      cancel: "Cancel",
      confirm: "Confirm",
      delete: "Delete",
      save: "Save",
    },
    onboarding: {
      title: "Daily Compass",
      subtitle: "Your daily direction in a few minutes.",
      description:
        "Pause for a moment, check your state and choose the best direction for today: act, rest, stay the course, change strategy or pause.",
      points: [
        "Check your energy, pressure and clarity.",
        "Get a simple daily decision.",
        "Finish with more calm and focus.",
      ],
      cta: "Continue as guest",
      guestNote:
        "No account is created. Your data stays locally on this device.",
      settingsLink: "Settings",
    },
    settings: {
      title: "Settings",
      subtitle: "Your preferences and data.",
      language: {
        heading: "Language",
        description: "Choose your app language.",
      },
      premium: {
        heading: "Premium — coming soon",
        description:
          "Longer history, weekly and monthly reports and deeper AI reflections will be available in the future.",
      },
      about: {
        heading: "About",
        description:
          "An app for daily reflection and choosing your direction. Your data stays local on your device. This is not a medical or therapeutic tool.",
        version: "Version 0.1.0 MVP",
      },
      legal: {
        heading: "Legal",
        privacy: "Privacy policy",
        terms: "Terms of use",
        deleteData: "Delete data / account",
        aiNotice: "AI notice",
      },
      data: {
        heading: "Data",
        description: "All your data is stored locally in this browser.",
        deleteButton: "Delete local data",
        confirmTitle: "Delete all data?",
        confirmDescription:
          "This will remove all your check-ins and settings from this device. This cannot be undone.",
        confirmAction: "Yes, delete",
        toastDeleted: "Local data has been deleted.",
      },
    },
    placeholder: {
      today: {
        title: "Today's check-in",
        note: "Your daily compass will appear here soon.",
      },
      result: {
        title: "Today's result",
        note: "Your decision and recommendation will show up here after the check-in.",
      },
      dashboard: {
        title: "Dashboard",
        note: "A view of your trends and balance. Coming soon.",
      },
      history: {
        title: "History",
        note: "Your past check-ins will appear here.",
      },
      reflection: {
        title: "Reflection",
        note: "A deeper reflection on your week. Coming soon.",
      },
    },
    legal: {
      privacy: {
        title: "Privacy policy",
        note: "Full content is coming soon.",
      },
      terms: {
        title: "Terms of use",
        note: "Full content is coming soon.",
      },
      deleteData: {
        title: "Delete data",
        note: "Data deletion instructions will appear here soon.",
      },
      aiNotice: {
        title: "AI notice",
        note: "Information about AI use will appear here soon.",
      },
    },
    checkin: {
      title: "Check-in",
      submit: "Show my direction",
      submitLoading: "One moment…",
      counter: "{count}/240",
      alreadyDone: {
        eyebrow: "Your direction for today",
        title: "Today's check-in is done",
        description:
          "Your direction for today is already chosen. You can view it or redo the check-in.",
        view: "View result",
        redo: "Redo check-in",
      },
      questions: {
        energy: {
          question: "How much energy do you have today?",
          min: "1 — very low",
          max: "5 — very high",
        },
        mood: {
          question: "What is your mood today?",
          min: "1 — heavy",
          max: "5 — light/good",
        },
        pressure: {
          question: "How much pressure do you feel today?",
          min: "1 — almost none",
          max: "5 — very strong",
        },
        clarity: {
          question: "Do you have a clear priority today?",
          min: "1 — not clear at all",
          max: "5 — very clear",
        },
        planRealism: {
          question: "Is today's plan realistic?",
          min: "1 — unrealistic",
          max: "5 — very realistic",
        },
        need: {
          question: "What do you need most today?",
          options: {
            action: "action",
            rest: "rest",
            direction: "staying the course",
            strategy: "strategy correction",
            pause: "pausing",
          },
        },
        dailyWin: {
          question: "What one thing would make today feel like a win?",
          placeholder: "You can skip this.",
        },
      },
    },
    result: {
      eyebrow: "Your direction for today",
      balanceLabel: "Daily balance",
      recommendationLabel: "Recommendation",
      dailyWinLabel: "Your win today",
      toastSaved: "Saved",
      actions: {
        redo: "Redo check-in",
        dashboard: "Go to dashboard",
      },
      labels: {
        act: {
          title: "Act",
          description:
            "You have enough energy and clarity today to move forward.",
        },
        rest: {
          title: "Rest",
          description:
            "Today may require a slower rhythm and recovery.",
        },
        stay: {
          title: "Stay the course",
          description:
            "You do not need a revolution today. The best move is calm continuation.",
        },
        change: {
          title: "Change strategy",
          description:
            "Today's plan may need correction, simplification or rescheduling.",
        },
        pause: {
          title: "Pause",
          description: "Today may be about recovering clarity.",
        },
      },
    },
    dashboard: {
      title: "Dashboard",
      today: "Today",
      viewDetails: "View details",
      newCheckin: "New check-in",
      redoCheckin: "Redo check-in",
      emptyState: {
        message:
          "You haven't done today's check-in yet. It takes less than a minute.",
        cta: "Do check-in",
      },
      strip: {
        heading: "Last 7 days",
        empty: "No entry",
      },
      weeklySummary: {
        heading: "Weekly summary",
        template:
          "This week your most common direction was: {result}. {phase}",
        averageBalance: "Average balance: {value}/100",
        notEnough:
          "Not enough data yet for a weekly summary. Come back after a few check-ins.",
        phases: {
          act: "This may mean you are in a phase of action — you have the conditions to move things forward.",
          rest: "This may mean you are in a phase of recovery — you may be choosing a slower rhythm day by day.",
          stay: "This may mean you are in a phase of continuation — you're keeping the course without needing a revolution.",
          change:
            "This may mean you are in a phase of adjustment — plans need simplifying and rescheduling.",
          pause:
            "This may mean you are in a phase of pausing — you're recovering clarity and your compass.",
        },
      },
    },
    history: {
      title: "History",
      empty: {
        message:
          "Your history will appear here after your first saved check-in.",
        cta: "Do your first check-in",
      },
      item: {
        recommendationLabel: "Recommendation",
        answersLabel: "Answers",
        winLabel: "Win of the day",
        reflectionLabel: "Reflection",
        showMore: "Show more",
        showLess: "Hide",
      },
      answers: {
        energy: "Energy",
        mood: "Mood",
        pressure: "Pressure",
        clarity: "Clarity",
        planRealism: "Plan realism",
        need: "Need",
      },
      premiumTeaser:
        "Longer history and monthly reports will be available in Premium — coming soon.",
    },
    reflection: {
      title: "Reflection",
      headerToday: "Today's reflection",
      headerOther: "Reflection: {date}",
      textareaPlaceholder: "Write your thoughts — no one else will see them.",
      saveButton: "Save reflection",
      counter: "{count}/600",
      empty: {
        message: "Before you reflect, do your first daily check-in.",
        cta: "Do check-in",
      },
      saved: {
        heading: "Your note from this day",
        edit: "Edit",
      },
      toastSaved: "Reflection saved",
      questionsIntro: "A few prompts that may help:",
      questions: {
        act: [
          "What one thing would truly move you forward today?",
          "What can you postpone to avoid scattering your energy?",
        ],
        rest: [
          "What truly needs to happen today, and what can wait?",
          "How can you reduce pressure without guilt?",
        ],
        stay: [
          "What small step is enough for today?",
          "What will help you avoid overcomplicating a simple day?",
        ],
        change: [
          "Which part of the plan feels too heavy today?",
          "What can you simplify, move or remove?",
        ],
        pause: [
          "What do you need most right now: silence, order, a break or a decision?",
          "What is the smallest calm step you can take?",
        ],
      },
    },
    premium: {
      badge: { soon: "Coming soon" },
      cta: {
        seeIncludes: "See what Premium includes",
        learnMore: "Learn more",
      },
      sections: {
        features: "What's coming in Premium",
      },
      hero: {
        title: "Daily Compass Premium",
        subtitle:
          "More history, reports and deeper reflections — coming soon.",
        description: {
          p1: "Premium will help you better understand your patterns of energy, pressure, clarity and daily direction. It's designed for people who want to see patterns, revisit reflections and access deeper insights.",
          p2: "The free version remains fully useful day to day. Premium is meant to deepen the experience, not take away the core value of the app.",
        },
      },
      features: {
        fullHistory: {
          title: "Full history",
          description:
            "Access to 30, 90 days and full history — with the ability to revisit past reflections and entries.",
        },
        weeklyReports: {
          title: "Weekly reports",
          description:
            "Weekly summaries with result frequencies and average balance score.",
        },
        monthlyReports: {
          title: "Monthly reports",
          description:
            "Monthly reports showing trends and changes in your rhythm.",
        },
        patternAnalysis: {
          title: "Pattern analysis",
          description:
            "Gentle insights about patterns of energy, pressure, clarity and daily balance.",
        },
        deeperReflections: {
          title: "Deeper AI reflections",
          description:
            "More expansive reflections and gentle interpretation of your patterns — still in calm, human language.",
        },
        customQuestions: {
          title: "Custom check-in questions",
          description:
            "Add your own questions to the daily check-in and personalize your ritual.",
        },
        dayModes: {
          title: "Day modes",
          description:
            "Choose a mode that fits your situation: Founder Mode, Calm Day Mode, Deep Work Mode, Recovery Mode, Decision Mode.",
        },
        exportNotes: {
          title: "Export notes & reports",
          description:
            "Export your reflections, notes and reports to an external file.",
        },
        gentleReminders: {
          title: "Gentle reminders",
          description:
            "Calm reminders for your check-in and weekly summary — no aggressive notifications.",
        },
      },
      dayModes: {
        founder: "Founder Mode",
        calmDay: "Calm Day Mode",
        deepWork: "Deep Work Mode",
        recovery: "Recovery Mode",
        decision: "Decision Mode",
      },
      sync: {
        title: "Sync & backup",
        description:
          "Sync and backup across devices — being considered as a future option. The free version stays fully offline and local.",
      },
      closing:
        "Premium is not meant to take away the value of the free version. You'll always be able to do today's check-in and see your daily direction — that stays the heart of the app.",
      settingsCard: {
        heading: "Daily Compass Premium",
        subtitle:
          "More history, reports and deeper reflections — coming soon.",
      },
      dashboardTeaser: {
        title: "Monthly report",
        subtitle: "Your patterns and trends from the last 30 days.",
      },
      historyTeaser:
        "Unlock longer history and monthly reports in Daily Compass Premium — coming soon.",
      reflectionTeaser:
        "Deeper AI reflections and pattern analysis will be available in Daily Compass Premium.",
    },
  },
};
